import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bridalconnect.co.za';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const auth = {
  login: (data) => api.post('/api/auth/login', data),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPasswordWithToken: (token, newPassword) => api.post('/api/auth/reset-password-token', { token, newPassword })
};

// Providers
export const providers = {
  getAll: (params = {}) => api.get('/api/providers', { params }),
  getById: (id) => api.get(`/api/providers/${id}`),
  getProfile: () => api.get('/api/providers/profile'),
  updateProfile: (data) => api.put('/api/providers/profile', data),
  addCustomField: (data) => api.post('/api/providers/fields', data),
  publishProfile: () => api.put('/api/providers/profile/publish')
};

// Categories
export const categories = {
  getAll: () => api.get('/api/categories'),
  getWithCounts: () => api.get('/api/categories/with-counts')
};

// Media endpoints
export const media = {
  upload: (type, formData) => api.post(`/api/media/${type}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  addYouTubeVideo: (url, caption) => api.post('/api/media/youtube', { url, caption }),
  scrapeWebsite: (websiteUrl, options = {}) => api.post('/api/media/scrape-website', {
    websiteUrl,
    ...options
  }),
  importFromWebsite: (images, videos = []) => api.post('/api/media/import-from-website', { images, videos }),
  getProviderMedia: (providerId) => api.get(`/api/media/provider/${providerId}`),
  deleteMedia: (mediaId) => api.delete(`/api/media/${mediaId}`),
  reorderMedia: (mediaOrder) => api.put('/api/media/reorder', { mediaOrder })
};

// Admin endpoints
export const admin = {
  getAllProviders: () => api.get('/api/admin/providers'),
  createProvider: (data) => api.post('/api/admin/providers/create', data),
  impersonate: (providerId) => api.post(`/api/admin/impersonate/${providerId}`),
  unpublishProvider: (providerId) => api.put(`/api/providers/${providerId}/unpublish`),
  deleteProvider: (providerId, confirmationCode) => api.delete(`/api/providers/${providerId}`, { data: { confirmationCode } })
};

// Claims & Applications
export const claims = {
  request: (data) => api.post('/api/claims/request', data),
  apply: (data) => api.post('/api/claims/apply', data),
  getPending: () => api.get('/api/claims/pending'),
  getAll: (status) => api.get('/api/claims/all', { params: { status } }),
  approve: (id) => api.post(`/api/claims/${id}/approve`),
  reject: (id) => api.post(`/api/claims/${id}/reject`)
};

// Site settings endpoints
export const settings = {
  getAll: () => api.get('/api/settings'),
  get: (key) => api.get(`/api/settings/${key}`),
  update: (settings) => api.put('/api/settings', { settings }),
  uploadHeroImage: (formData) => api.post('/api/settings/hero-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
};

// --- Couple / Wedding Pages ---

// Separate axios instance for couple auth
const coupleApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

coupleApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('coupleToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const coupleAuth = {
  register: (data) => api.post('/api/couple-auth/register', data),
  login: (data) => api.post('/api/couple-auth/login', data),
  logout: () => {
    localStorage.removeItem('coupleToken');
    localStorage.removeItem('coupleUser');
  },
  getCurrentUser: () => {
    const user = localStorage.getItem('coupleUser');
    return user ? JSON.parse(user) : null;
  },
  getMe: () => coupleApi.get('/api/couple-auth/me'),
  updateMe: (data) => coupleApi.put('/api/couple-auth/me', data),
  forgotPassword: (email) => api.post('/api/couple-auth/forgot-password', { email }),
  resetPasswordWithToken: (token, newPassword) => api.post('/api/couple-auth/reset-password-token', { token, newPassword }),
  checkSlug: (slug) => api.get(`/api/couple-auth/check-slug/${slug}`)
};

export const wedding = {
  // Public
  getPage: (slug) => api.get(`/api/wedding/page/${slug}`),
  verifyPagePassword: (slug, password) => api.post(`/api/wedding/page/${slug}/verify-password`, { password }),
  getRsvpForm: (token) => api.get(`/api/wedding/rsvp/${token}`),
  submitRsvp: (token, data) => api.post(`/api/wedding/rsvp/${token}`, data),
  lookupRsvp: (slug, name) => api.post(`/api/wedding/rsvp-lookup/${slug}`, { name }),
  guestUpload: (slug, formData) => api.post(`/api/wedding/media/guest-upload/${slug}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Couple authenticated
  getMyPage: () => coupleApi.get('/api/wedding/my-page'),
  updateMyPage: (data) => coupleApi.put('/api/wedding/my-page', data),
  setPagePassword: (password) => coupleApi.put('/api/wedding/my-page/password', { password }),
  publishPage: (is_published) => coupleApi.put('/api/wedding/my-page/publish', { is_published }),
  uploadHero: (formData) => coupleApi.post('/api/wedding/my-page/hero', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Sections
  updateSections: (sections) => coupleApi.put('/api/wedding/sections', { sections }),
  updateSection: (id, data) => coupleApi.put(`/api/wedding/section/${id}`, data),

  // Events
  getEvents: () => coupleApi.get('/api/wedding/events'),
  createEvent: (data) => coupleApi.post('/api/wedding/events', data),
  updateEvent: (id, data) => coupleApi.put(`/api/wedding/events/${id}`, data),
  deleteEvent: (id) => coupleApi.delete(`/api/wedding/events/${id}`),

  // Wedding party
  getParty: () => coupleApi.get('/api/wedding/party'),
  addPartyMember: (formData) => coupleApi.post('/api/wedding/party', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updatePartyMember: (id, formData) => coupleApi.put(`/api/wedding/party/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePartyMember: (id) => coupleApi.delete(`/api/wedding/party/${id}`),

  // Guests
  getGuests: () => coupleApi.get('/api/wedding/guests'),
  addGuest: (data) => coupleApi.post('/api/wedding/guests', data),
  importGuests: (guests) => coupleApi.post('/api/wedding/guests/import', { guests }),
  updateGuest: (id, data) => coupleApi.put(`/api/wedding/guests/${id}`, data),
  deleteGuest: (id) => coupleApi.delete(`/api/wedding/guests/${id}`),
  getWhatsAppLink: (id) => coupleApi.get(`/api/wedding/guests/${id}/whatsapp-link`),

  // RSVP dashboard
  getRsvpDashboard: () => coupleApi.get('/api/wedding/rsvp-dashboard'),

  // Media
  getMedia: () => coupleApi.get('/api/wedding/media'),
  uploadMedia: (formData) => coupleApi.post('/api/wedding/media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  reorderMedia: (order) => coupleApi.put('/api/wedding/media/reorder', { order }),
  approveMedia: (id, is_approved) => coupleApi.put(`/api/wedding/media/${id}/approve`, { is_approved }),
  deleteMedia: (id) => coupleApi.delete(`/api/wedding/media/${id}`),

  // Vendors
  getVendors: () => coupleApi.get('/api/wedding/vendors'),
  addVendor: (data) => coupleApi.post('/api/wedding/vendors', data),
  removeVendor: (id) => coupleApi.delete(`/api/wedding/vendors/${id}`)
};

export default api;
